import classNames from 'classnames';
import React, {useMemo} from 'react';

import type {DiscoveryUnit} from '@udemy/discovery-api';
import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {useI18n} from '@udemy/i18n';
import {
    CertificationVoucherBundleUnitCarousel,
    trackVoucherOfferPresented,
    type CertificationBundle,
    type VoucherUIRegion,
} from '@udemy/react-certification-preparation';
import {
    selectPools,
    useExamAvailability,
    useVoucherPricing,
} from '@udemy/react-certification-vouchers';
import {ExperimentImpressionEvent, useFeatures} from '@udemy/react-experimentation-provider';

import {
    CourseSubscriptionAvailabilityStatus,
    GetCertificationVoucherExamBySlugQuery,
    useCoursesSubscriptionAvailabilityQuery,
    CertificationVoucherProviderType,
    VoucherPoolProductType,
} from 'src/generated/graphql';
import {
    CERT_VOUCHERS_SHOW_ADD_TO_CART,
    SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT,
} from 'src/lib/experimentation-constants';

import {SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS} from '../topic/constants';
import {Banner, VoucherBulletPointIcon} from './components/banner/banner.react-component';
import {CoursesToGetYouStarted} from './components/courses-to-get-you-started/courses-to-get-you-started.react-component';
import {Description} from './components/description/description.react-component';
import {trackViewLandingPage} from './events';
import {usePoolIdsForEvent} from './hooks/use-pool-ids-for-event';
import styles from './voucher-landing-page.module.less';

// TODO CER-256: Delete once stock issue has been resolved.
// See the TODO near the usage of this constant for more details.
const AWS_SPECIALTY_PROFESSIONAL_VOUCHER_POOL_ID = '6';
const AWS_ASSOCIATE_AND_FOUNDATIONAL_VOUCHER_POOL_IDS: (string | undefined)[] = ['5'];

export type VoucherData = {
    exam: NonNullable<GetCertificationVoucherExamBySlugQuery['certificationVoucherExamBySlug']>;
    /** "Courses to get you started" unit */
    coursesToGetYouStartedUnit?: DiscoveryUnit | null;
    certificationBundles: CertificationBundle[];
};

export const VoucherLandingPage = ({voucherData}: {voucherData: VoucherData}) => {
    const {exam, coursesToGetYouStartedUnit, certificationBundles} = voucherData;
    const {interpolate, ngettext, gettext} = useI18n();

    const hasBundle = useMemo(() => {
        return certificationBundles.length > 0;
    }, [certificationBundles.length]);

    // TODO: CER-437: Delete this as part of the cleanup where the bullet points will stored in the backend.
    const isLinuxFoundationPage = useMemo(
        () => exam.providerDetails.type === CertificationVoucherProviderType.LinuxFoundation,
        [exam.providerDetails.type],
    );

    const bundleCourseIds = certificationBundles.flatMap((bundle) =>
        bundle.contents.map((course) => String(course.id)),
    );
    const {data: bundleSubscriptionData, isLoading: isBundleSubscriptionLoading} =
        useCoursesSubscriptionAvailabilityQuery(
            {ids: bundleCourseIds},
            {enabled: bundleCourseIds.length > 0},
        );
    const allCoursesExcludedFromSubscription =
        !isBundleSubscriptionLoading &&
        (bundleSubscriptionData?.courses ?? []).every(
            (course) =>
                course?.subscriptionInfo?.availabilityStatus ===
                CourseSubscriptionAvailabilityStatus.None,
        );

    const {isLoading: isFeaturesLoading, featureVariantAssignments} = useFeatures([
        CERT_VOUCHERS_SHOW_ADD_TO_CART,
        SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT,
    ]);
    const certVouchersFeature = featureVariantAssignments.get(CERT_VOUCHERS_SHOW_ADD_TO_CART);

    const certificationVoucherBundleUnitFeature = featureVariantAssignments.get(
        SHOW_CERTIFICATION_VOUCHER_BUNDLE_UNIT,
    );
    const showCertificationVoucherBundleUnit =
        certificationVoucherBundleUnitFeature?.configuration?.showCertificationVoucherBundleUnit ??
        false;

    const voucherAvailability = useExamAvailability(exam.id);
    const {primaryPool, retakeInsurancePool, defaultPool, hasBothPoolsAvailable} =
        selectPools(exam);

    const isUdemyIssuedRetake =
        retakeInsurancePool?.productType === VoucherPoolProductType.UdemyIssuedRetakeInsurance;

    const {
        isLoading: isVoucherPoolPricesLoading,
        singlePoolPrice,
        retakeInsurancePrice,
        defaultPrice,
        hasDiscount,
        discountPercentage,
        hasStock: pricingHasStock,
        effectiveHasBothPoolsAvailable,
    } = useVoucherPricing({
        voucherPool: primaryPool,
        retakeInsurancePool,
        defaultPool,
        hasBothPoolsAvailable,
        isExamLoading: voucherAvailability === undefined,
    });

    // Preserve undefined while exam availability is loading so Banner renders a skeleton
    const hasStock: boolean | undefined =
        voucherAvailability === undefined || isVoucherPoolPricesLoading
            ? undefined
            : pricingHasStock;

    const firstBulletPoint = (() => {
        // TODO CER 256: Delete this once stock issue is resolved.
        // See https://udemy.slack.com/archives/C09QZERRWMB/p1773102042467569 for more details.
        if (
            exam.voucherPools &&
            exam.voucherPools.some(
                (voucherPool) => voucherPool?.id === AWS_SPECIALTY_PROFESSIONAL_VOUCHER_POOL_ID,
            )
        ) {
            return gettext(
                'Exam Voucher (one attempt) is valid until December 10, 2026. For Exam voucher + Retake (two attempts), the initial exam attempt must be completed by December 31, 2026. The retake exam must then be taken by January 31, 2027.',
            );
        }

        // TODO CER 256: Delete this once stock issue is resolved.
        // See https://udemy.slack.com/archives/C09QZERRWMB/p1773102042467569 for more details.
        if (
            exam.voucherPools &&
            exam.voucherPools.some((voucherPool) =>
                AWS_ASSOCIATE_AND_FOUNDATIONAL_VOUCHER_POOL_IDS.includes(voucherPool?.id),
            )
        ) {
            return gettext(
                'Exam Voucher (one attempt) is valid for at least 9 months after purchase. For Exam voucher + Retake (two attempts), the initial exam attempt must be completed by December 31, 2026. The retake exam must then be taken by January 31, 2027.',
            );
        }

        if (effectiveHasBothPoolsAvailable) {
            return gettext(
                'Voucher and Retake (if purchased) valid for at least 9 months after purchase.',
            );
        }

        return interpolate(
            ngettext(
                'At least %(months)s-month validity after purchase',
                'At least %(months)s-months validity after purchase',
                exam.voucherPools?.[0]?.expirationThresholdInMonths ?? 12,
            ),
            {
                months: exam.voucherPools?.[0]?.expirationThresholdInMonths ?? 12,
            },
            true,
        );
    })();

    const poolIdsForEvent = usePoolIdsForEvent({
        exam,
        voucherAvailability,
        retakeInsurancePrice,
        defaultPrice,
        isVoucherPoolPricesLoading,
    });

    const trackFunc = React.useCallback(() => {
        trackViewLandingPage('425.00', '382.50', '382.50');
        trackVoucherOfferPresented(
            {
                name: exam.title,
                examId: exam.id,
                poolId: '',
                voucherId: null,
            },
            poolIdsForEvent.length > 0 ? poolIdsForEvent : [exam.id],
            'voucher_landing' as VoucherUIRegion,
        );
        if (certVouchersFeature) {
            Tracker.publishEvent(
                new ExperimentImpressionEvent(
                    certVouchersFeature.experimentIds,
                    certVouchersFeature.isLocalDefault,
                ),
            );
        }
    }, [exam.title, exam.id, poolIdsForEvent, certVouchersFeature]);

    const content = (
        <section aria-labelledby="voucher-landing-page-heading">
            <Banner
                voucherId={exam.id}
                isUdemyIssuedRetake={isUdemyIssuedRetake}
                content={{
                    title: exam.title,
                    description: exam.description ?? '',
                    logoSrc: exam.retakePatternImageUrl ?? exam.imageUrl ?? '',
                    bulletPoints: [
                        ...(!isLinuxFoundationPage
                            ? [
                                  {
                                      icon: VoucherBulletPointIcon.Date,
                                      text: firstBulletPoint,
                                  },
                              ]
                            : []),
                        ...(exam.valuePropositions
                            ?.filter((proposition): proposition is string => proposition !== null)
                            ?.map((proposition) => ({
                                icon: VoucherBulletPointIcon.Doc,
                                text: proposition,
                            })) ?? []),
                    ],
                }}
                pricing={{
                    singlePoolPrice,
                    retakeInsurancePrice,
                    defaultPrice,
                    isLoading: isVoucherPoolPricesLoading,
                }}
                availability={{
                    hasStock,
                    isAvailableForRegion: voucherAvailability?.isAvailableForRegion,
                    hasBothPoolsAvailable: effectiveHasBothPoolsAvailable,
                }}
                poolIds={{
                    poolId: primaryPool?.id ?? '',
                    retakeInsurancePoolId: retakeInsurancePool?.id,
                    defaultPoolId: defaultPool?.id,
                }}
            />

            <Description
                questionAnswers={exam.questionsAndAnswers.map((qa) => ({
                    question: qa.question,
                    answerHTML: qa.answer,
                }))}
                discountPercentage={discountPercentage.toString()}
                hasDiscount={hasDiscount}
                examTitle={exam.title}
                examId={exam.id}
                examProviderType={exam.providerDetails.type}
                poolId={primaryPool?.id ?? ''}
                isUdemyIssuedRetake={isUdemyIssuedRetake}
            />
            {hasBundle && !isFeaturesLoading && allCoursesExcludedFromSubscription && (
                <TrackImpression
                    trackFunc={() => {
                        if (certificationVoucherBundleUnitFeature) {
                            const {experimentIds = [], isLocalDefault = false} =
                                certificationVoucherBundleUnitFeature;
                            Tracker.publishEvent(
                                new ExperimentImpressionEvent(experimentIds, isLocalDefault),
                            );
                        }
                    }}
                >
                    <div />
                </TrackImpression>
            )}
            {!isFeaturesLoading &&
                showCertificationVoucherBundleUnit &&
                hasBundle &&
                allCoursesExcludedFromSubscription && (
                    <div
                        className={classNames(
                            'ud-container',
                            styles['voucher-bundle-unit-container'],
                        )}
                    >
                        <FunnelLogContextProvider pageType="individualVoucher">
                            <CertificationVoucherBundleUnitCarousel
                                title={gettext('Get Certified with Top Instructors')}
                                subtitle={gettext(
                                    'Explore curated bundles from expert instructors. Browse to compare teaching styles and prep strategies.',
                                )}
                                units={certificationBundles.map((bundle) => ({
                                    bundle: bundle,
                                    showCommonInstructor: true,
                                    ...SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS,
                                }))}
                            />
                        </FunnelLogContextProvider>
                    </div>
                )}
            <CoursesToGetYouStarted
                coursesToGetYouStartedUnit={coursesToGetYouStartedUnit}
                redirectTopicUrl={exam?.topic?.url}
            />
        </section>
    );

    // Only wrap with TrackImpression after pricing has loaded
    // This ensures poolIdsForEvent has the correct value before the event is sent
    if (isVoucherPoolPricesLoading) {
        return content;
    }

    return (
        <TrackImpression trackFunc={trackFunc} visibilityThreshold={0.01}>
            {content}
        </TrackImpression>
    );
};
